/**
 * ReporterPanel -- Tabbed container for Bug Report + Suggestion / Feedback.
 *
 * Wraps the existing BugReportPanel and the new FeedbackPanel behind a
 * two-tab bar. See GitLab issue #86 for the UX spec: keep the single
 * Toolbox icon affordance, let the user self-select whether they're
 * reporting a bug or giving feedback.
 *
 * The 'bug-report' panel id is retained so persisted user preferences
 * (jotai atom + any stored activePanel) keep working across the upgrade.
 */

import { useState, useRef, useCallback } from 'react'
import { Bug, Lightbulb } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { BugReportPanel } from './BugReportPanel'
import { FeedbackPanel } from './FeedbackPanel'

type Tab = 'bug' | 'feedback'

const TAB_ORDER: readonly Tab[] = ['bug', 'feedback']

export function ReporterPanel() {
  const [tab, setTab] = useState<Tab>('bug')
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({
    bug: null,
    feedback: null,
  })

  // Standard tab-roving arrow keys. Horizontal tablist: Left/Right move
  // focus and switch the active tab; Home/End jump to ends.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = TAB_ORDER.indexOf(tab)
      let nextIndex = currentIndex
      if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TAB_ORDER.length
      else if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length
      else if (e.key === 'Home') nextIndex = 0
      else if (e.key === 'End') nextIndex = TAB_ORDER.length - 1
      else return
      e.preventDefault()
      const nextTab = TAB_ORDER[nextIndex]
      setTab(nextTab)
      tabRefs.current[nextTab]?.focus()
    },
    [tab],
  )

  return (
    <div className="flex flex-col h-full">
      <div
        role="tablist"
        aria-label="Report type"
        onKeyDown={handleKeyDown}
        className="flex shrink-0 border-b border-ue-border-medium bg-ue-bg-darker"
      >
        <TabButton
          active={tab === 'bug'}
          onClick={() => setTab('bug')}
          icon={<Bug size={12} />}
          label="Report a Bug"
          testId="reporter-tab-bug"
          buttonRef={(el) => {
            tabRefs.current.bug = el
          }}
        />
        <TabButton
          active={tab === 'feedback'}
          onClick={() => setTab('feedback')}
          icon={<Lightbulb size={12} />}
          label="Suggestion"
          testId="reporter-tab-feedback"
          buttonRef={(el) => {
            tabRefs.current.feedback = el
          }}
        />
      </div>
      <div className="flex-1 min-h-0">
        {tab === 'bug' ? <BugReportPanel /> : <FeedbackPanel />}
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  testId,
  buttonRef,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  testId: string
  buttonRef: (el: HTMLButtonElement | null) => void
}) {
  return (
    <button
      ref={buttonRef}
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'flex-1 flex items-center justify-center gap-1.5 px-2 py-2 transition-colors border-b-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-ue-accent-primary/50',
        active
          ? 'border-ue-accent-primary text-ue-text-primary'
          : 'border-transparent text-ue-text-secondary hover:text-ue-text-primary hover:bg-white/5',
      )}
      style={{ fontSize: '11px' }}
    >
      {icon}
      {label}
    </button>
  )
}
